import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

export type SelectFieldValue = string | Record<string, unknown>;

export interface SelectFieldProps<TItem, TValue extends SelectFieldValue = SelectFieldValue> {
  value: TValue | null;
  onChange: (newValue: TValue, item: TItem) => void;
  items: TItem[];
  getValue?: (item: TItem) => TValue;
  getLabel?: (item: TItem) => React.ReactNode;
  renderOption?: (args: {
    item: TItem;
    value: TValue;
    label: React.ReactNode;
    selected: boolean;
  }) => React.ReactNode;
  renderValue?: (args: {
    item: TItem | null;
    value: TValue | null;
    label: React.ReactNode;
  }) => React.ReactNode;
  areValuesEqual?: (a: TValue | null, b: TValue | null) => boolean;
  getValueKey?: (value: TValue) => string;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  optionsClassName?: string;
  openDirection?: 'top' | 'bottom';
}

function defaultGetLabel<TItem, TValue extends SelectFieldValue>(
  item: TItem,
  value: TValue,
): React.ReactNode {
  if (typeof item === 'string') return item;
  if (typeof value === 'string') return value;
  return '[Select]';
}

function defaultGetValue<TItem, TValue extends SelectFieldValue>(item: TItem): TValue {
  return item as unknown as TValue;
}

export function SelectField<TItem, TValue extends SelectFieldValue = SelectFieldValue>({
  value,
  onChange,
  items,
  getValue = defaultGetValue,
  getLabel,
  renderOption,
  renderValue,
  areValuesEqual,
  getValueKey,
  placeholder = 'Select…',
  disabled = false,
  className,
  optionsClassName,
  openDirection = 'bottom',
}: SelectFieldProps<TItem, TValue>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || disabled) return;

    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;

      const target = e.target as Node | null;
      if (target && root.contains(target)) return;

      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    // Use capture so it still works even if something stops propagation inside overlays
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, disabled]);

  const equals = useMemo(() => {
    if (areValuesEqual) return areValuesEqual;
    if (getValueKey) {
      return (a: TValue | null, b: TValue | null) => {
        if (a === null || b === null) return a === b;
        return getValueKey(a) === getValueKey(b);
      };
    }
    return (a: TValue | null, b: TValue | null) => Object.is(a, b);
  }, [areValuesEqual, getValueKey]);

  const normalized = useMemo(() => {
    return items.map((item) => {
      const v = getValue(item);
      const label = getLabel ? getLabel(item) : defaultGetLabel(item, v);
      return { item, value: v, label };
    });
  }, [items, getValue, getLabel]);

  const selected = useMemo(() => {
    const found = normalized.find((x) => equals(x.value, value));
    return found ?? null;
  }, [normalized, equals, value]);

  const selectedLabel = selected?.label ?? placeholder;

  const selectFieldClassNames = twMerge('relative py-3 px-5', className);
  const optionsClassNames = twMerge(
    classNames(
      'ds-legacy__ul',
      'shadow-ds-select-field-options rounded-xl py-3! px-2! gap-3 flex flex-col min-w-56 absolute z-10 left-8 bg-ds-bg-surface-soft-2',
      'invisible opacity-0 transition-opacity duration-200 ease-in-out [&.opened]:opacity-100 [&.opened]:visible',
      {
        opened: open,
        'top-[calc(100%_-_40px)]': openDirection === 'top',
        'bottom-[calc(100%_-_40px)]': openDirection === 'bottom',
      },
      optionsClassName,
    ),
  );

  return (
    <div ref={rootRef} className={selectFieldClassNames}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="hover:cursor-pointer"
      >
        {renderValue
          ? renderValue({
              item: selected?.item ?? null,
              value: selected?.value ?? null,
              label: selectedLabel,
            })
          : selectedLabel}
      </button>

      <ul role="listbox" className={optionsClassNames}>
        {normalized.map(({ item, value: optionValue, label }, idx) => {
          const isSelected = equals(optionValue, value);

          return (
            <li
              key={getValueKey ? getValueKey(optionValue) : `${idx}`}
              role="option"
              aria-selected={isSelected}
            >
              <button
                type="button"
                className={
                  classNames(
                    'py-2 cursor-pointer w-full text-left text-ds-text-1 rounded-md transition-all duration-400 ease-in-out',
                    'hover:bg-ds-bg-surface-2/10',
                    '[.selected]:bg-ds-bg-surface-soft-3',
                    { selected: isSelected },
                  ) + ''
                }
                onClick={() => {
                  onChange(optionValue, item);
                  setOpen(false);
                }}
              >
                {renderOption
                  ? renderOption({ item, value: optionValue, label, selected: isSelected })
                  : label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
